import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import LabelledInputLayout from './LabelledInputLayout';
import CircleCheck from '../icons/CircleCheck';
import TrashCan from '../../../common/icons/TrashCan';

const FileInputField = ({
  id,
  label = '',
  isRequired = false,
  className = '',
  setError = () => {},
  errorMsg = [],
  touched = false,
  onChange = () => {},
  value = '',
  validFileTypes = null,
  maxSizeInBytes = null,
}) => {
  // The two helper functions below are used to update the display name of the logo and header
  // files, so the file names in the form stay consistent.
  // These files are saved using Cloudinary. Cloudinary appends string to the end of the file name
  // to enforce uniqueness.
  // `removeSuffixFromFilename` removes the appended string after the file name is received from the
  // backend, and before it is displayed in the form.
  // `appendSuffixToFilename` appends the "fake" substring to the end of the file name that is
  // selected from the file manager, so that `removeSuffixFromFilename` removes appended suffix
  // instead of part of the original file name in this case.
  const appendSuffixToFilename = (filename) => filename.replace(/\.([^.]+$)/, '_suffix.$1');
  const removeSuffixFromFilename = (filename) => filename.replace(/_[^._]+(?=\.[^.]+$)/, '');
  const fileInputRef = useRef(null);
  const fileName = removeSuffixFromFilename(value?.split('/').pop());

  const validateInput = (uploadedFile) => {
    // Validate file type
    if (validFileTypes && !validFileTypes.includes(uploadedFile.type)) {
      return 'Please upload a file with the correct format.';
    }

    // Validate file size
    if (maxSizeInBytes && uploadedFile.size > maxSizeInBytes) {
      return 'Please upload a file with the correct file size.';
    }
    return '';
  };

  const handleChange = (e) => {
    const fileObj = e.target.files[0] ?? {};
    const validationError = validateInput(fileObj);
    if (validationError) {
      setError(validationError);
      fileInputRef.current.value = null;
    } else {
      setError(null);
      const fileObjName = appendSuffixToFilename(fileObj.name);
      onChange({
        target: {
          name: id,
          value: fileObjName ?? '',
        },
      });
    }
  };

  const clearFileInput = (e) => {
    e.preventDefault();
    setError(null);
    fileInputRef.current.value = '';
    onChange({
      target: {
        name: id,
        value: '',
      },
    });
  };

  return (
    <LabelledInputLayout
      id={id}
      label={label}
      isRequired={isRequired}
      className={className}
      errorMsg={errorMsg}
      touched={touched}
    >
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <input
          aria-label={label}
          id={id}
          name={id}
          className="pressrelease-file-input styled-file-input"
          type="file"
          ref={fileInputRef}
          onChange={handleChange}
          // eslint-disable-next-line
          style={{position: 'absolute', zIndex: '100', top: 0, left: 0, opacity: 0, width: '100%', cursor: 'pointer'}}
        />
        <div className="embed-input" style={{ marginBottom: 0 }}>
          {fileName && (
            <div className="embed-input__icon">
              <CircleCheck />
            </div>
          )}
          <input
            aria-label="File name"
            className="embed-input__field"
            type="text"
            value={fileName || 'No file chosen'}
            readOnly
            style={{ cursor: 'default' }}
          />
          {fileName && (
            <button aria-label="Clear File" className="embed-input__button" onClick={clearFileInput} type="button" style={{ zIndex: '200' }}>
              <TrashCan />
            </button>
          )}
        </div>
      </div>
    </LabelledInputLayout>
  );
};

FileInputField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  className: PropTypes.string,
  errorMsg: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  touched: PropTypes.bool,
  onChange: PropTypes.func,
  setError: PropTypes.func,
  value: PropTypes.string,
  validFileTypes: PropTypes.arrayOf(PropTypes.string),
  maxSizeInBytes: PropTypes.number,
};

export default FileInputField;
